import httpClient, { normalizeError } from "./httpClient";

const defaultStatus = {
  has_custom_partnerships: false,
  active_partnerships_count: 0,
  pending_invites: 0,
};

const formatError = (
  error,
  fallback = "Something went wrong. Please try again.",
) => {
  const normalized = normalizeError(error);
  return new Error(normalized.message || fallback);
};

export const getPartnershipStatus = async () => {
  try {
    const data = await httpClient.get("/api/partnerships/status");
    if (!data) {
      return { ...defaultStatus };
    }
    return {
      ...defaultStatus,
      ...data,
      has_custom_partnerships:
        data.has_custom_partnerships ??
        Boolean(
          data.active_partnerships_count && data.active_partnerships_count > 0,
        ),
    };
  } catch (error) {
    if (error?.status === 404) {
      return { ...defaultStatus };
    }
    // Don't throw on status endpoint - just return defaults
    console.warn("Partnership status unavailable:", error.message);
    return { ...defaultStatus };
  }
};

export const getBusinessPartnerships = async () => {
  try {
    return await httpClient.get("/api/partnerships");
  } catch (error) {
    throw formatError(error, "Unable to load custom partnerships");
  }
};

export const getBusinessB2BPartnerships = async (businessId) => {
  if (!businessId) {
    throw new Error("Business ID is required to load B2B partnerships");
  }
  try {
    return await httpClient.get(
      `/api/partnerships/business/${businessId}/b2b-partnerships`,
    );
  } catch (error) {
    throw formatError(error, "Unable to load B2B partnerships");
  }
};

export const inviteCustomPartner = async (payload) => {
  try {
    return await httpClient.post("/api/partnerships/invite", payload);
  } catch (error) {
    throw formatError(error, "Unable to send invitation");
  }
};

export const updateBusinessPartnershipStatus = async (
  partnershipId,
  status,
) => {
  try {
    // Use PATCH with the correct endpoint
    return await httpClient.patch(`/api/partnerships/${partnershipId}`, {
      status,
    });
  } catch (error) {
    throw formatError(error, "Unable to update partnership");
  }
};

export const getPartnerDeals = async (config = {}) => {
  try {
    // Use the correct endpoint path that matches the backend
    return await httpClient.get("/api/partnerships/my-partnerships", config);
  } catch (error) {
    throw formatError(error, "Unable to load your partnerships");
  }
};

export const getPartnershipLedger = async (partnershipId) => {
  try {
    return await httpClient.get(`/api/partnerships/${partnershipId}/leads`);
  } catch (error) {
    throw formatError(error, "Unable to load partnership ledger");
  }
};

export const getInviteByToken = async (token) => {
  try {
    return await httpClient.get(`/api/invites/${token}`);
  } catch (error) {
    throw formatError(error, "This invitation could not be found");
  }
};

export const acceptInviteByToken = async (token, payload = null) => {
  try {
    const hasPayload =
      payload instanceof FormData ||
      (payload &&
        typeof payload === "object" &&
        Object.keys(payload).length > 0);
    return hasPayload
      ? await httpClient.post(`/api/invites/${token}/accept`, payload)
      : await httpClient.post(`/api/invites/${token}/accept`);
  } catch (error) {
    throw formatError(error, "Unable to accept this invitation");
  }
};
